import React, { useState } from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import dayjs from "dayjs";
import { materialsymbolsdownload, wg_report_logo } from "images";

/**
 * Download menu for the Base and Heat Factors Report.
 * Only Excel and CSV are supported for this report.
 */
const BaseAndHeatFactorReportDownload = ({ data = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const reportTitle = "Base and Heat Factors Report";

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const formatDate = (value) => (value ? dayjs(value).format("MM/DD/YYYY") : "-");

  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Base and Heat Factors");

    // Logo
    try {
      const response = await fetch(wg_report_logo);
      const imageBlob = await response.blob();
      const base64 = await convertToBase64(imageBlob);
      const imageId = workbook.addImage({ base64, extension: "png" });
      worksheet.addImage(imageId, {
        tl: { col: 5, row: 0 },
        ext: { width: 160, height: 60 },
      });
    } catch (error) {
      // Logo is non-critical — proceed without it if it can't be fetched.
    }

    worksheet.getColumn(1).width = 22;
    worksheet.getColumn(2).width = 18;
    worksheet.getColumn(3).width = 30;
    worksheet.getColumn(4).width = 18;
    worksheet.getColumn(5).width = 16;
    worksheet.getColumn(6).width = 16;
    worksheet.getColumn(7).width = 16;
    worksheet.getRow(1).height = 60;

    worksheet.mergeCells("A1:B1");
    const titleCell = worksheet.getCell("A1");
    titleCell.value = `Report Name: ${reportTitle}`;
    titleCell.font = { bold: true, size: 14 };
    titleCell.alignment = { vertical: "middle", horizontal: "left" };

    const infoRow = worksheet.addRow([`Date Published:`, dayjs().format("MM/DD/YYYY")]);
    infoRow.eachCell((cell) => {
      cell.font = { bold: true };
    });

    worksheet.addRow([]);

    const headerRow = worksheet.addRow([
      "Account Number",
      "Rate Category",
      "Installation Number",
      "Entry Value",
      "Operand",
      "Valid From",
      "Valid To",
    ]);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F3557" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    data.forEach((item) => {
      const row = worksheet.addRow([
        item.AccountNumber,
        item.RateCategory ?? "-",
        item.InstallationNumber,
        item.EntryValue ?? "-",
        item.Operand ?? "-",
        formatDate(item.ValidFrom),
        formatDate(item.ValidTo),
      ]);
      row.eachCell((cell, colNumber) => {
        if (colNumber >= 4) {
          cell.alignment = { horizontal: "right" };
        }
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "BaseAndHeatFactor_Report.xlsx");
    handleClose();
  };

  const convertToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const downloadCSV = () => {
    const headerInfo = [
      ["Base and Heat Factors Report"],
      [],
      ["Date Published:", dayjs().format("MM/DD/YYYY")],
      [],
    ];

    const headers = [
      "Account Number",
      "Rate Category",
      "Installation Number",
      "Entry Value",
      "Operand",
      "Valid From",
      "Valid To",
    ];

    const csvRows = [];

    headerInfo.forEach((row) => {
      csvRows.push(row.map((field) => `"${field}"`).join(","));
    });

    csvRows.push(headers.map((field) => `"${field}"`).join(","));

    data.forEach((item) => {
      const row = [
        item.AccountNumber ?? "",
        item.RateCategory ?? "",
        item.InstallationNumber ?? "",
        item.EntryValue ?? "",
        item.Operand ?? "",
        formatDate(item.ValidFrom),
        formatDate(item.ValidTo),
      ];
      csvRows.push(row.map((field) => `"${field}"`).join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "BaseAndHeatFactor_Report.csv");
    handleClose();
  };

  return (
    <>
      <Button
        variant="contained"
        className="Download"
        color="primary"
        onClick={handleClick}
      >
        <img src={materialsymbolsdownload} alt="Download" /> Download
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={downloadExcel}>Excel</MenuItem>
        <MenuItem onClick={downloadCSV}>CSV</MenuItem>
      </Menu>
    </>
  );
};

export default BaseAndHeatFactorReportDownload;